import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  RCA_CROSS_CHECK_FINISHED,
  RCA_CROSS_CHECK_FAILED,
  CASCO_CROSS_CHECK_FINISHED,
  CASCO_CROSS_CHECK_FAILED
} from 'features/settings/subscriptions';
import { useConditionalSubscription } from 'features/common/security/hooks';
import { useInsuranceTypeConfiguration } from 'hooks';
import { CircularProgress, TableCell, TableRow, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Warning } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCrossCheckUtils } from './utils';
import RcaCrossCheckComparatorModal from './rcaChecks/RcaCrossCheckComparatorModal';
import CascoCrossCheckComparatorModal from './cascoChecks/CascoCrossCheckComparatorModal';
import { insuranceTypeNames } from 'constants/defaultValues';
import LoadingWrapper from 'components/common/LoadingWrapper';
import { Button } from '@totalsoft/rocket-ui';

const CrossCheckFlowResultContainer = ({ data, insuranceTypeCode }) => {
  const { t } = useTranslation();
  const { watchCrossCheckStatus, updateCrossCheckResultStatus } = useCrossCheckUtils();
  const { cascoInsuranceTypeConfiguration, rcaInsuranceTypeConfiguration } = useInsuranceTypeConfiguration(
    new Array(insuranceTypeNames.CASCO, insuranceTypeNames.RCA)
  );
  const [crossCheckResult, setCrossCheckResult] = useState(data);
  const [loading, setLoading] = useState(true);
  const [showComparator, setShowComparator] = useState(false);

  useEffect(() => {
    const statusSubscription = watchCrossCheckStatus(status =>
      setLoading(status.find(x => x.documentId === crossCheckResult.documentId)?.loading)
    );
    return () => statusSubscription.unsubscribe();
  }, [watchCrossCheckStatus, crossCheckResult]);

  useConditionalSubscription(RCA_CROSS_CHECK_FINISHED, {
    variables: { documentId: crossCheckResult.documentId },
    skip: !insuranceTypeCode === rcaInsuranceTypeConfiguration?.PropertyCode,
    onData: ({ data: { data } }) => {
      if (data?.rcaCrossCheckFinished) {
        const { documentId, documentNumber, documentDate, existingQuotations, newQuotations } = data.rcaCrossCheckFinished;
        setCrossCheckResult(prev => ({
          ...prev,
          documentId,
          documentNumber,
          documentDate,
          existingQuotations,
          newQuotations,
          success: true
        }));
      }
      updateCrossCheckResultStatus(crossCheckResult);
    }
  });

  useConditionalSubscription(RCA_CROSS_CHECK_FAILED, {
    variables: { documentId: crossCheckResult.documentId },
    skip: !insuranceTypeCode === rcaInsuranceTypeConfiguration?.PropertyCode,
    onData: ({ data: { data } }) => {
      setCrossCheckResult(prev => ({ ...prev, error: data?.rcaCrossCheckFailed?.errorMessage, success: false }));
      updateCrossCheckResultStatus(crossCheckResult);
    }
  });

  useConditionalSubscription(CASCO_CROSS_CHECK_FINISHED, {
    variables: { documentId: crossCheckResult.documentId },
    skip: !insuranceTypeCode === cascoInsuranceTypeConfiguration?.PropertyCode,
    onData: ({ data: { data } }) => {
      if (data?.cascoCrossCheckFinished) {
        const { crossCheckResult, existingQuotations, newQuotations } = data.cascoCrossCheckFinished;
        setCrossCheckResult(prev => ({
          ...prev,
          ...crossCheckResult,
          existingQuotations,
          newQuotations,
          success: true
        }));
      }
      updateCrossCheckResultStatus(crossCheckResult);
    }
  });

  useConditionalSubscription(CASCO_CROSS_CHECK_FAILED, {
    variables: { documentId: crossCheckResult.documentId },
    skip: !insuranceTypeCode === cascoInsuranceTypeConfiguration?.PropertyCode,
    onData: ({ data: { data } }) => {
      setCrossCheckResult(prev => ({ ...prev, error: data?.cascoCrossCheckFailed?.errorMessage, success: false }));
      updateCrossCheckResultStatus(crossCheckResult);
    }
  });

  const resultIcon = useMemo(() => {
    return (
      <>
        {loading ? (
          <CircularProgress color='primary' />
        ) : crossCheckResult.success ? (
          <Tooltip title={t('Settings.InsuranceIntegrationChecks.Tooltips.Success')}>
            <CheckCircleIcon color='primary' />
          </Tooltip>
        ) : (
          <Tooltip title={crossCheckResult.error}>
            <Warning color='warning' />
          </Tooltip>
        )}
      </>
    );
  }, [loading, crossCheckResult, t]);

  return (
    <>
      <TableRow key={crossCheckResult.documentId}>
        <TableCell>
          <a href={`/insurance-documents/${crossCheckResult.documentId}`} rel='noopener noreferrer' target='_blank'>
            {t('Offer.List.Field.DocumentNumberHeader', {
              documentNumber: crossCheckResult.documentNumber,
              documentDate: t('DATE', {
                date: crossCheckResult.documentDate
              })
            })}
          </a>
        </TableCell>
        <TableCell>
          <LoadingWrapper loading={loading}>{resultIcon}</LoadingWrapper>
        </TableCell>
        <TableCell sx={{ textAlign: 'end' }}>
          <Button
            id='crossCheck_compareQuotations'
            disabled={loading || !crossCheckResult.success}
            color='primary'
            onClick={() => setShowComparator(true)}
          >
            {t('Settings.InsuranceIntegrationChecks.CompareQuotations')}
          </Button>
        </TableCell>
      </TableRow>
      {showComparator &&
        (insuranceTypeCode === rcaInsuranceTypeConfiguration?.PropertyCode ? (
          <RcaCrossCheckComparatorModal
            onCloseModal={() => setShowComparator(false)}
            existingQuotations={crossCheckResult.existingQuotations}
            newQuotations={crossCheckResult.newQuotations}
          />
        ) : insuranceTypeCode === cascoInsuranceTypeConfiguration?.PropertyCode ? (
          <CascoCrossCheckComparatorModal
            onCloseModal={() => setShowComparator(false)}
            existingQuotations={crossCheckResult.existingQuotations}
            newQuotations={crossCheckResult.newQuotations}
          />
        ) : (
          <></>
        ))}
    </>
  );
};

CrossCheckFlowResultContainer.propTypes = {
  data: PropTypes.object,
  insuranceTypeCode: PropTypes.string
};
export default CrossCheckFlowResultContainer;
